import { RTCInboundRtpVideoStreamStats } from './types';
import { Queue } from 'utility/queue';
import { StreamSelectorBase } from './StreamSelectorBase';

function getQPByIndex(stats: RTCInboundRtpStreamStats[], index: number) {
    //statsが更新されていない場合-1を返す
    const currentTimestamp = new Date().getTime();
    const last = stats.at(- 1);
    if (!last) return 'deferred';
    if ((currentTimestamp - last.timestamp) > 50) return 'deferred';

    const target = stats.at(-index - 1);
    const prev = stats.at(-index - 1);
    if (!target || !prev) return 'deferred';

    const framesDecoded0 = target.framesDecoded ?? 0;
    const framesDecoded1 = prev.framesDecoded ?? 0;
    const deltaFrames = framesDecoded0 - framesDecoded1;
    if (deltaFrames < 1) return 'deferred'; // each framesDecoded must be integer

    if (target.qpSum === undefined || prev.qpSum === undefined) {
        return 'matched';
    }

    const qpSum0 = target.qpSum ?? 0;
    const qpSum1 = prev.qpSum ?? 0;
    return Math.floor((qpSum0 - qpSum1) / deltaFrames);
}

function maxIndex<T>(xs: [T, ...T[]]) {
    let maxIndex = 0;

    for (let i = 1; i < xs.length; ++i) {
        if (xs[maxIndex] < xs[i]) {
            maxIndex = i;
        }
    }

    return maxIndex;
}

function isNonEmpty<T>(xs: T[]): xs is [T, ...T[]] {
    return xs.length > 0;
}

export class StreamSelectorByQP extends StreamSelectorBase {
    private qpByStream = new Array<Queue<RTCInboundRtpVideoStreamStats>>();

    label() { return 'qp'; }

    description() {
        return this.context;
    }

    batchUpdate(streams: Queue<RTCInboundRtpVideoStreamStats>[]): void {
        streams.forEach((stats, index) => {
            const qps = this.qpByStream[index]
                ?? (() => this.qpByStream[index] = new Queue(2))();

            const prev = stats.at(-2);
            if (prev !== undefined) {
                qps.push(prev);
            }

            const last = stats.at(-1);
            if (last !== undefined) {
                qps.push(last);
            }
        });
    }

    private context: Record<string, unknown> = {};

    get(): number | 'matched' | 'deferred' {
        const statsTable = this.qpByStream;

        this.context = { stats: statsTable.map(q => [...q.array]) };

        const lastQPs = statsTable.map(stats => getQPByIndex(stats.array, 0));
        this.context = { ...this.context, lastQPs };
        if (!isNonEmpty(lastQPs) || lastQPs.includes('deferred')) return 'deferred';
        if (lastQPs.includes('matched')) return 'matched';
        const bestStreamIndexInLastStat = maxIndex(lastQPs);
        this.context = { ...this.context, bestStreamIndexInLastStat };

        const prevQPs = statsTable.map(stats => getQPByIndex(stats.array, 1));
        this.context = { ...this.context, prevQPs };
        if (!isNonEmpty(prevQPs) || prevQPs.includes('deferred')) return 'deferred';
        if (prevQPs.includes('matched')) return 'matched';
        const bestStreamIndexInPrevStat = maxIndex(prevQPs);
        this.context = { ...this.context, bestStreamIndexInPrevStat };

        if (bestStreamIndexInLastStat === bestStreamIndexInPrevStat) {
            return bestStreamIndexInPrevStat;
        }

        return 'matched';
    }
}
